import { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import type {
  SkillHeatmapEntry,
  EmployeeSkillEntry,
  DevelopmentGoalEntry,
  GoalSuggestion,
  SkillDiffResult,
  SkillHistoryEntry,
  TrainingResource,
  SkillDemandSnapshotEntry,
} from '../types';
import type { EmployeeListItem } from '../api/employees';
import {
  fetchSkillHeatmap,
  fetchEmployeeSkills,
  fetchEmployeeGoals,
  fetchGoalSuggestions,
  createGoal,
  updateGoal,
  deleteGoal,
  fetchSkillDiff,
  fetchSkillHistory,
  updateSkillProficiency,
  fetchSkillNames,
} from '../api/skillDevelopment';
import { fetchEmployees } from '../api/employees';
import { fetchAllTraining, fetchSkillTrends } from '../api/training';
import SkillBadge from '../components/SkillBadge';

type TabKey = 'heatmap' | 'goals' | 'skills' | 'progress' | 'training' | 'trends';

const TABS: { key: TabKey; labelKey: string }[] = [
  { key: 'heatmap', labelKey: 'skillDev.tabHeatmap' },
  { key: 'goals', labelKey: 'skillDev.tabGoals' },
  { key: 'skills', labelKey: 'skillDev.tabSkills' },
  { key: 'progress', labelKey: 'skillDev.tabProgress' },
  { key: 'training', labelKey: 'skillDev.tabTraining' },
  { key: 'trends', labelKey: 'skillDev.tabTrends' },
];

export default function SkillDevelopment() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabKey>('heatmap');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {t('skillDev.title', 'Skill Development')}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          {t('skillDev.subtitle', 'Analyse skill levels, set development goals, and track progress across the organisation.')}
        </p>
      </div>

      <nav aria-label={t('skillDev.tabNavigation', 'Skill development sections')} className="border-b border-gray-200">
        <div className="flex gap-4 -mb-px" role="tablist">
          {TABS.map(({ key, labelKey }) => (
            <button
              key={key}
              role="tab"
              type="button"
              aria-selected={activeTab === key}
              onClick={() => setActiveTab(key)}
              className={[
                'whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium transition-colors',
                activeTab === key
                  ? 'border-brand-500 text-brand-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
              ].join(' ')}
            >
              {t(labelKey, key)}
            </button>
          ))}
        </div>
      </nav>

      {activeTab === 'heatmap' && <HeatmapTab />}
      {activeTab === 'goals' && <GoalsTab />}
      {activeTab === 'skills' && <SkillsTab />}
      {activeTab === 'progress' && <ProgressTab />}
      {activeTab === 'training' && <TrainingTab />}
      {activeTab === 'trends' && <TrendsTab />}
    </div>
  );
}

// -------------------------------------------------------------------------
// Tab 1: Skill Heatmap
// -------------------------------------------------------------------------

function HeatmapTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState<SkillHeatmapEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [groupBy, setGroupBy] = useState('officeLocation');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchSkillHeatmap(groupBy)
      .then((result) => { if (!cancelled) setData(result); })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [groupBy]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (data.length === 0) return <EmptyState message={t('skillDev.noHeatmapData', 'No skill proficiency data available yet. Skills are populated when CVs are processed.')} />;

  // Build matrix: departments as rows, categories as columns
  const departments = [...new Set(data.map((d) => d.department))].sort();
  const categories = [...new Set(data.map((d) => d.category))].sort();
  const lookup = new Map(data.map((d) => [`${d.department}|${d.category}`, d.average_proficiency]));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          {t('skillDev.heatmapDesc', 'Average skill proficiency by department and category (1 = Awareness, 5 = Expert).')}
        </p>
        <div className="flex items-center gap-2">
          <label htmlFor="heatmap-group-by" className="text-sm font-medium text-gray-700">
            {t('skillDev.groupBy', 'Group by')}
          </label>
          <Select
            aria-labelledby="heatmap-group-by"
            options={[
              { value: 'department', label: t('skillDev.department', 'Department') },
              { value: 'jobTitle', label: t('skillDev.jobTitle', 'Job Title') },
              { value: 'officeLocation', label: t('skillDev.officeLocation', 'Office Location') },
            ]}
            value={{ value: groupBy, label: groupBy === 'jobTitle' ? t('skillDev.jobTitle', 'Job Title') : groupBy === 'officeLocation' ? t('skillDev.officeLocation', 'Office Location') : t('skillDev.department', 'Department') }}
            onChange={(opt) => setGroupBy(opt?.value ?? 'department')}
            classNamePrefix="react-select"
            isSearchable={false}
            styles={{
              control: (base) => ({ ...base, fontSize: '0.875rem', minHeight: '38px', minWidth: '180px' }),
              option: (base) => ({ ...base, fontSize: '0.875rem' }),
            }}
          />
        </div>
      </div>
      <div className="rounded-lg border border-gray-200" style={{ overflow: 'auto', maxWidth: 'calc(100vw - 20rem)' }}>
        <table className="w-max divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500 sticky left-0 bg-gray-50 z-10 min-w-[160px]">
                {t('skillDev.department', 'Department')}
              </th>
              {categories.map((cat) => (
                <th key={cat} className="px-1 pt-2 pb-3 text-center font-medium text-gray-500 align-bottom" style={{ height: '140px' }}>
                  <div className="flex items-end justify-center h-full">
                    <span className="whitespace-nowrap text-xs" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
                      {cat}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {departments.map((dept) => (
              <tr key={dept}>
                <td className="px-4 py-3 font-medium text-gray-900 sticky left-0 bg-white z-10 min-w-[160px]">
                  {dept}
                </td>
                {categories.map((cat) => {
                  const val = lookup.get(`${dept}|${cat}`);
                  return (
                    <td key={cat} className="px-3 py-3 text-center">
                      {val != null ? (
                        <button
                          type="button"
                          onClick={() => navigate(`/skill-development/detail?groupBy=${encodeURIComponent(groupBy)}&group=${encodeURIComponent(dept)}&category=${encodeURIComponent(cat)}`)}
                          className={[
                            'inline-flex items-center justify-center w-10 h-10 rounded-lg text-sm font-bold cursor-pointer transition-transform hover:scale-110 hover:shadow-md',
                            val >= 4.0 ? 'bg-green-100 text-green-800' :
                            val >= 3.0 ? 'bg-blue-100 text-blue-800' :
                            val >= 2.0 ? 'bg-amber-100 text-amber-800' :
                            'bg-red-100 text-red-800',
                          ].join(' ')}
                          title={`${dept} - ${cat}: ${val.toFixed(1)} — ${t('skillDev.clickToDetail', 'Click to see employee details')}`}
                        >
                          {val.toFixed(1)}
                        </button>
                      ) : (
                        <span className="text-gray-300" aria-label={t('skillDev.noData', 'No data')}>-</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// Tab 2: Development Goals
// -------------------------------------------------------------------------

function GoalsTab() {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState<EmployeeListItem[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [goals, setGoals] = useState<DevelopmentGoalEntry[]>([]);
  const [suggestions, setSuggestions] = useState<GoalSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchEmployees({ page: 1, page_size: 200, status: 'ACTIVE' })
      .then((result) => { if (!cancelled) setEmployees(result.items); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingEmployees(false); });
    return () => { cancelled = true; };
  }, []);

  const loadGoals = useCallback((empId: string) => {
    if (!empId) return;
    setSelectedId(empId);
    setLoading(true);
    setError(null);
    Promise.all([fetchEmployeeGoals(empId), fetchGoalSuggestions(empId)])
      .then(([g, s]) => { setGoals(g); setSuggestions(s); })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const handleStatusChange = useCallback(async (goalId: string, status: string) => {
    try {
      await updateGoal(goalId, { status });
      if (selectedId) loadGoals(selectedId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update goal');
    }
  }, [selectedId, loadGoals]);

  const handleDelete = useCallback(async (goalId: string) => {
    try {
      await deleteGoal(goalId);
      if (selectedId) loadGoals(selectedId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete goal');
    }
  }, [selectedId, loadGoals]);

  const handleAcceptSuggestion = useCallback(async (suggestion: GoalSuggestion) => {
    if (!selectedId) return;
    try {
      await createGoal(selectedId, {
        skillName: suggestion.skill_name,
        currentLevel: suggestion.current_level,
        targetLevel: suggestion.suggested_target_level,
        targetDate: new Date(Date.now() + 180 * 86400000).toISOString().split('T')[0],
        notes: suggestion.reason,
        source: 'PERSONA_GAP',
        personaId: suggestion.persona_id,
      });
      loadGoals(selectedId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create goal');
    }
  }, [selectedId, loadGoals]);

  if (loadingEmployees) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      <EmployeeSelector
        employees={employees}
        selectedId={selectedId}
        onChange={loadGoals}
      />

      {loading && <LoadingSpinner />}
      {error && <ErrorMessage message={error} />}

      {selectedId && !loading && (
        <div className="space-y-6">
          {/* Active Goals */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900">
                {t('skillDev.activeGoals', 'Development Goals')}
              </h2>
              <button
                type="button"
                onClick={() => setShowAddForm(!showAddForm)}
                className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                {showAddForm ? t('skillDev.cancel', 'Cancel') : t('skillDev.addGoal', '+ Add Goal')}
              </button>
            </div>

            {showAddForm && (
              <AddGoalForm
                employeeId={selectedId}
                onCreated={() => { setShowAddForm(false); loadGoals(selectedId); }}
                onCancel={() => setShowAddForm(false)}
              />
            )}

            {goals.length === 0 ? (
              <EmptyState message={t('skillDev.noGoals', 'No development goals set. Use suggestions below or add manually.')} />
            ) : (
              <div className="space-y-3">
                {goals.map((goal) => (
                  <div key={goal.id} className={[
                    'rounded-xl border p-4',
                    goal.status === 'COMPLETED' ? 'border-green-200 bg-green-50' :
                    goal.status === 'CANCELLED' ? 'border-gray-200 bg-gray-50 opacity-60' :
                    'border-gray-200 bg-white',
                  ].join(' ')}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="font-semibold text-gray-900 max-w-[200px] truncate" title={goal.skill_name}>{goal.skill_name}</span>
                        <span className={[
                          'rounded-full px-2 py-0.5 text-xs font-medium',
                          goal.status === 'ACTIVE' ? 'bg-blue-100 text-blue-700' :
                          goal.status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                          goal.status === 'CANCELLED' ? 'bg-gray-100 text-gray-600' :
                          'bg-red-100 text-red-700',
                        ].join(' ')}>
                          {goal.status}
                        </span>
                        {goal.suggested_by !== 'MANUAL' && (
                          <span className="text-xs text-gray-400">{t('skillDev.suggestedBy', 'via')} {goal.suggested_by}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {goal.status === 'ACTIVE' && (
                          <button
                            type="button"
                            onClick={() => handleStatusChange(goal.id, 'COMPLETED')}
                            className="text-xs text-green-600 hover:text-green-800 font-medium"
                          >
                            {t('skillDev.markComplete', 'Complete')}
                          </button>
                        )}
                        {goal.status === 'ACTIVE' && (
                          <button
                            type="button"
                            onClick={() => handleStatusChange(goal.id, 'CANCELLED')}
                            className="text-xs text-gray-400 hover:text-gray-600"
                          >
                            {t('skillDev.cancelGoal', 'Cancel')}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleDelete(goal.id)}
                          className="text-xs text-red-400 hover:text-red-600"
                          aria-label={t('skillDev.deleteGoal', 'Delete goal')}
                        >
                          {t('common.delete', 'Delete')}
                        </button>
                      </div>
                    </div>

                    {/* Progress bar */}
                    <div className="mt-2 flex items-center gap-3">
                      <span className="text-xs text-gray-500 w-20">
                        {t('skillDev.level', 'Level')} {goal.current_level} → {goal.target_level}
                      </span>
                      <div className="flex-1 h-2 rounded-full bg-gray-200">
                        <div
                          className="h-2 rounded-full bg-brand-500 transition-all"
                          style={{ width: `${Math.min(100, (goal.current_level / goal.target_level) * 100)}%` }}
                        />
                      </div>
                      {goal.target_date && (
                        <span className="text-xs text-gray-400">
                          {t('skillDev.dueBy', 'Due')}: {goal.target_date}
                        </span>
                      )}
                    </div>

                    {goal.notes && (
                      <p className="mt-2 text-xs text-gray-500">{goal.notes}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Suggestions */}
          {suggestions.length > 0 && (
            <div>
              <h3 className="text-md font-semibold text-gray-700 mb-2">
                {t('skillDev.suggestions', 'Suggested Goals (from Persona Gap Analysis)')}
              </h3>
              <div className="space-y-2">
                {suggestions.map((s) => (
                  <div key={`${s.skill_name}-${s.persona_id}`} className="flex items-center justify-between rounded-lg border border-dashed border-brand-300 bg-brand-50 p-3">
                    <div>
                      <span className="font-medium text-gray-900 max-w-[200px] truncate" title={s.skill_name}>{s.skill_name}</span>
                      <span className="ml-2 text-xs text-gray-500">
                        {t('skillDev.level', 'Level')} {s.current_level} → {s.suggested_target_level}
                      </span>
                      <p className="text-xs text-gray-500 mt-0.5">{s.reason}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleAcceptSuggestion(s)}
                      className="shrink-0 rounded-md bg-brand-600 px-3 py-1 text-xs font-medium text-white hover:bg-brand-700"
                    >
                      {t('skillDev.accept', 'Accept')}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AddGoalForm({ employeeId, onCreated, onCancel }: { employeeId: string; onCreated: () => void; onCancel: () => void }) {
  const { t } = useTranslation();
  const [skillNames, setSkillNames] = useState<string[]>([]);
  const [skillName, setSkillName] = useState('');
  const [targetLevel, setTargetLevel] = useState(3);
  const [targetDate, setTargetDate] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSkillNames().then(setSkillNames).catch(() => {});
  }, []);

  const skillOptions = useMemo(() =>
    skillNames.map((s) => ({ value: s, label: s })),
    [skillNames],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!skillName) return;
    setSaving(true);
    try {
      await createGoal(employeeId, {
        skillName: skillName.trim(),
        currentLevel: 0,
        targetLevel,
        targetDate: targetDate || undefined,
        notes: notes || undefined,
      });
      onCreated();
    } catch (_err) {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="rounded-lg border border-brand-200 bg-brand-50 p-4 mb-4 space-y-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label id="goal-skill-label" className="block text-sm font-medium text-gray-700">
            {t('skillDev.skillName', 'Skill Name')}
          </label>
          <Select
            aria-labelledby="goal-skill-label"
            options={skillOptions}
            value={skillOptions.find((o) => o.value === skillName) ?? null}
            onChange={(opt) => setSkillName(opt?.value ?? '')}
            placeholder={t('admin.selectSkill', 'Select a skill...')}
            isClearable
            classNamePrefix="react-select"
            styles={{
              control: (base) => ({ ...base, fontSize: '0.875rem', minHeight: '38px', marginTop: '0.25rem' }),
              option: (base) => ({ ...base, fontSize: '0.875rem' }),
            }}
          />
        </div>
        <div>
          <label htmlFor="goal-level" className="block text-sm font-medium text-gray-700">
            {t('skillDev.targetLevel', 'Target Level')} ({targetLevel})
          </label>
          <input
            id="goal-level"
            type="range"
            min={1}
            max={5}
            value={targetLevel}
            onChange={(e) => setTargetLevel(Number(e.target.value))}
            className="mt-2 w-full"
          />
        </div>
        <div>
          <label htmlFor="goal-date" className="block text-sm font-medium text-gray-700">
            {t('skillDev.targetDate', 'Target Date')}
          </label>
          <input
            id="goal-date"
            type="date"
            value={targetDate}
            onChange={(e) => setTargetDate(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-sm"
          />
        </div>
        <div>
          <label htmlFor="goal-notes" className="block text-sm font-medium text-gray-700">
            {t('skillDev.notes', 'Notes')}
          </label>
          <input
            id="goal-notes"
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-sm"
          />
        </div>
      </div>
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={saving}
          className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          {saving ? t('common.saving', 'Saving...') : t('skillDev.createGoal', 'Create Goal')}
        </button>
        <button type="button" onClick={onCancel} className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
          {t('common.cancel', 'Cancel')}
        </button>
      </div>
    </form>
  );
}

// -------------------------------------------------------------------------
// Tab 3: Employee Skills
// -------------------------------------------------------------------------

function SkillsTab() {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState<EmployeeListItem[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [skills, setSkills] = useState<EmployeeSkillEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingSkill, setEditingSkill] = useState<string | null>(null);
  const [showAddSkill, setShowAddSkill] = useState(false);
  const [newSkillName, setNewSkillName] = useState('');
  const [newSkillLevel, setNewSkillLevel] = useState(1);
  const [addingSkill, setAddingSkill] = useState(false);
  const [allSkillNames, setAllSkillNames] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    fetchEmployees({ page: 1, page_size: 200, status: 'ACTIVE' })
      .then((result) => { if (!cancelled) setEmployees(result.items); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingEmployees(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    fetchSkillNames().then(setAllSkillNames).catch(() => {});
  }, []);

  const skillOptions = useMemo(() =>
    allSkillNames.map((s) => ({ value: s, label: s })),
    [allSkillNames],
  );

  const loadSkills = useCallback((empId: string) => {
    if (!empId) return;
    setSelectedId(empId);
    setLoading(true);
    setError(null);
    fetchEmployeeSkills(empId)
      .then(setSkills)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const handleAddSkill = useCallback(async () => {
    if (!selectedId || !newSkillName.trim()) return;
    if (skills.some((s) => s.skill_name.toLowerCase() === newSkillName.trim().toLowerCase())) {
      setError(t('skillDev.skillExists', 'This skill already exists for this employee.'));
      return;
    }
    setAddingSkill(true);
    setError(null);
    try {
      await updateSkillProficiency(selectedId, newSkillName.trim(), newSkillLevel);
      // Reload full list so category info comes from the server
      const refreshed = await fetchEmployeeSkills(selectedId);
      setSkills(refreshed);
      setNewSkillName('');
      setNewSkillLevel(1);
      setShowAddSkill(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add skill');
    } finally {
      setAddingSkill(false);
    }
  }, [selectedId, newSkillName, newSkillLevel, skills, t]);

  const handleProficiencyChange = useCallback(async (skillName: string, newLevel: number) => {
    if (!selectedId) return;
    try {
      const updated = await updateSkillProficiency(selectedId, skillName, newLevel);
      // Update in-place to avoid full reload and scroll jump
      setSkills((prev) => prev.map((s) =>
        s.skill_name === skillName ? { ...s, proficiency: updated.proficiency ?? newLevel } : s
      ));
      setEditingSkill(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update');
    }
  }, [selectedId]);

  if (loadingEmployees) return <LoadingSpinner />;

  // Group skills by category
  const grouped = skills.reduce<Record<string, EmployeeSkillEntry[]>>((acc, s) => {
    const cat = s.category ?? 'Other';
    (acc[cat] = acc[cat] ?? []).push(s);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <EmployeeSelector employees={employees} selectedId={selectedId} onChange={loadSkills} />

      {loading && <LoadingSpinner />}
      {error && <ErrorMessage message={error} />}

      {selectedId && !loading && (
        <div>
          {!showAddSkill ? (
            <button
              type="button"
              onClick={() => setShowAddSkill(true)}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700"
            >
              + {t('skillDev.addSkill', 'Add Skill')}
            </button>
          ) : (
            <div className="rounded-lg border border-brand-200 bg-brand-50 p-4 space-y-3">
              <h3 className="text-sm font-semibold text-gray-900">{t('skillDev.addNewSkill', 'Add New Skill')}</h3>
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[200px]">
                  <label id="new-skill-name-label" className="block text-xs font-medium text-gray-700 mb-1">
                    {t('skillDev.skillName', 'Skill Name')}
                  </label>
                  <Select
                    aria-labelledby="new-skill-name-label"
                    options={skillOptions}
                    value={skillOptions.find((o) => o.value === newSkillName) ?? null}
                    onChange={(opt) => setNewSkillName(opt?.value ?? '')}
                    placeholder={t('admin.selectSkill', 'Select a skill...')}
                    isClearable
                    classNamePrefix="react-select"
                    styles={{
                      control: (base) => ({ ...base, fontSize: '0.875rem', minHeight: '38px' }),
                      option: (base) => ({ ...base, fontSize: '0.875rem' }),
                    }}
                  />
                </div>
                <div>
                  <label htmlFor="new-skill-level" className="block text-xs font-medium text-gray-700 mb-1">
                    {t('skillDev.proficiencyLevel', 'Proficiency')}
                  </label>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((level) => (
                      <button
                        key={level}
                        type="button"
                        onClick={() => setNewSkillLevel(level)}
                        className={[
                          'w-9 h-9 rounded-lg text-sm font-bold transition-colors',
                          level <= newSkillLevel
                            ? level >= 4 ? 'bg-green-500 text-white' : level >= 3 ? 'bg-blue-500 text-white' : level >= 2 ? 'bg-amber-500 text-white' : 'bg-red-400 text-white'
                            : 'bg-gray-200 text-gray-500 hover:bg-gray-300',
                        ].join(' ')}
                        title={`Level ${level}`}
                      >
                        {level}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleAddSkill}
                    disabled={addingSkill || !newSkillName.trim()}
                    className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                  >
                    {addingSkill ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowAddSkill(false); setNewSkillName(''); setNewSkillLevel(1); }}
                    className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    {t('common.cancel', 'Cancel')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {selectedId && !loading && skills.length === 0 && !showAddSkill && (
        <EmptyState message={t('skillDev.noSkills', 'No skills recorded for this employee yet.')} />
      )}

      {selectedId && !loading && skills.length > 0 && (
        <div className="space-y-6">
          {/* Summary */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="card">
              <p className="text-sm text-gray-500">{t('skillDev.totalSkills', 'Total Skills')}</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">{skills.length}</p>
            </div>
            <div className="card">
              <p className="text-sm text-gray-500">{t('skillDev.avgProficiency', 'Avg Proficiency')}</p>
              <p className="mt-1 text-2xl font-bold text-brand-600">
                {(skills.reduce((sum, s) => sum + s.proficiency, 0) / skills.length).toFixed(1)}
              </p>
            </div>
            <div className="card">
              <p className="text-sm text-gray-500">{t('skillDev.categories', 'Categories')}</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">{Object.keys(grouped).length}</p>
            </div>
          </div>

          {/* Skills by category */}
          {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([category, catSkills]) => (
            <div key={category}>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">{category}</h3>
              <div className="space-y-2">
                {catSkills.sort((a, b) => b.proficiency - a.proficiency).map((skill) => (
                  <div key={skill.id} className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3">
                    <span className="w-40 font-medium text-gray-900 truncate" title={skill.skill_name}>
                      {skill.skill_name}
                    </span>
                    <div className="flex-1 flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((level) => (
                        <button
                          key={level}
                          type="button"
                          onClick={() => {
                            if (editingSkill === skill.skill_name) {
                              handleProficiencyChange(skill.skill_name, level);
                            } else {
                              setEditingSkill(skill.skill_name);
                            }
                          }}
                          className={[
                            'h-6 flex-1 rounded transition-colors',
                            level <= skill.proficiency
                              ? level >= 4 ? 'bg-green-500' : level >= 3 ? 'bg-blue-500' : level >= 2 ? 'bg-amber-500' : 'bg-red-400'
                              : 'bg-gray-200',
                            editingSkill === skill.skill_name ? 'cursor-pointer hover:opacity-80 ring-2 ring-brand-300' : '',
                          ].join(' ')}
                          aria-label={`${skill.skill_name} level ${level}`}
                          title={editingSkill === skill.skill_name ? `Set to level ${level}` : `Level ${level}`}
                        />
                      ))}
                    </div>
                    <span className="text-sm font-semibold text-gray-700 w-8 text-center">
                      {skill.proficiency}
                    </span>
                    <span className="text-xs text-gray-400 w-16 truncate" title={skill.source}>
                      {skill.source === 'CV_EXTRACTION' ? 'CV' : skill.source}
                    </span>
                    {editingSkill === skill.skill_name && (
                      <button
                        type="button"
                        onClick={() => setEditingSkill(null)}
                        className="text-xs text-gray-400 hover:text-gray-600"
                      >
                        {t('common.cancel', 'Cancel')}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// -------------------------------------------------------------------------
// Tab 4: Skill Progress
// -------------------------------------------------------------------------

function ProgressTab() {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState<EmployeeListItem[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [diff, setDiff] = useState<SkillDiffResult | null>(null);
  const [history, setHistory] = useState<SkillHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchEmployees({ page: 1, page_size: 200, status: 'ACTIVE' })
      .then((result) => { if (!cancelled) setEmployees(result.items); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingEmployees(false); });
    return () => { cancelled = true; };
  }, []);

  const loadProgress = useCallback((empId: string) => {
    if (!empId) return;
    setSelectedId(empId);
    setLoading(true);
    setError(null);
    Promise.all([fetchSkillDiff(empId), fetchSkillHistory(empId)])
      .then(([d, h]) => { setDiff(d); setHistory(h); })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  if (loadingEmployees) return <LoadingSpinner />;

  return (
    <div className="space-y-4">
      <EmployeeSelector employees={employees} selectedId={selectedId} onChange={loadProgress} />

      {loading && <LoadingSpinner />}
      {error && <ErrorMessage message={error} />}

      {selectedId && !loading && (
        <div className="space-y-6">
          {/* Skill Diff */}
          {diff && (diff.added.length > 0 || diff.removed.length > 0) ? (
            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <h3 className="font-semibold text-gray-900 mb-3">
                {t('skillDev.latestChanges', 'Latest Skill Changes')}
              </h3>
              {diff.added.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-medium text-green-700 mb-1">
                    {t('skillDev.newSkills', 'New Skills')} (+{diff.added.length})
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {diff.added.map((s) => <SkillBadge key={s} skill={s} matched />)}
                  </div>
                </div>
              )}
              {diff.removed.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-red-700 mb-1">
                    {t('skillDev.removedSkills', 'Removed from CV')} (-{diff.removed.length})
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {diff.removed.map((s) => <SkillBadge key={s} skill={s} matched={false} />)}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <EmptyState message={t('skillDev.noDiff', 'No skill changes detected yet. Upload a new CV to see progress.')} />
          )}

          {/* Skill History Timeline */}
          {history.length > 0 && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">
                {t('skillDev.extractionHistory', 'Extraction History')}
              </h3>
              <div className="space-y-3">
                {history.map((entry, idx) => (
                  <div key={entry.id} className={[
                    'rounded-lg border p-3',
                    idx === 0 ? 'border-brand-200 bg-brand-50' : 'border-gray-200 bg-white',
                  ].join(' ')}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {idx === 0 && (
                          <span className="inline-flex items-center rounded-full bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-700">
                            {t('skillDev.latest', 'Latest')}
                          </span>
                        )}
                        <span className="text-sm font-medium text-gray-900">
                          {entry.skill_count} {t('skillDev.skillsExtracted', 'skills extracted')}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(entry.extracted_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// -------------------------------------------------------------------------
// Tab 5: Training
// -------------------------------------------------------------------------

function TrainingTab() {
  const { t } = useTranslation();
  const [resources, setResources] = useState<TrainingResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchAllTraining()
      .then((data) => { if (!cancelled) setResources(data); })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (resources.length === 0) return <EmptyState message={t('skillDev.noTraining', 'No training resources configured yet.')} />;

  const grouped = resources.reduce<Record<string, TrainingResource[]>>((acc, r) => {
    (acc[r.skill_name] = acc[r.skill_name] ?? []).push(r);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">{t('skillDev.trainingDesc', 'Available training resources from Udemy Business and other providers.')}</p>
      {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([skill, items]) => (
        <div key={skill}>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">{skill}</h3>
          <div className="space-y-2">
            {items.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3">
                <div className="flex-1">
                  <span className="font-medium text-gray-900">{r.title}</span>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                    <span>{r.provider}</span>
                    {r.estimated_hours && <span>{r.estimated_hours}h</span>}
                    <span className="rounded-full bg-gray-100 px-2 py-0.5">{r.resource_type}</span>
                  </div>
                </div>
                {r.url && (
                  <a href={r.url} target="_blank" rel="noopener noreferrer" className="shrink-0 rounded-md bg-brand-600 px-3 py-1 text-xs font-medium text-white hover:bg-brand-700">
                    {t('skillDev.openCourse', 'Open')}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// -------------------------------------------------------------------------
// Tab 6: Trends
// -------------------------------------------------------------------------

function TrendsTab() {
  const { t } = useTranslation();
  const [data, setData] = useState<SkillDemandSnapshotEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchSkillTrends(12)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (data.length === 0) return <EmptyState message={t('skillDev.noTrends', 'No trend data yet. Snapshots are generated monthly.')} />;

  // Aggregate: top skills by latest demand
  const latestMonth = data.reduce((max, d) => d.snapshot_month > max ? d.snapshot_month : max, '');
  const latest = data.filter((d) => d.snapshot_month === latestMonth).sort((a, b) => b.demand_count - a.demand_count);

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">{t('skillDev.trendsDesc', 'Skill demand vs supply trends over time.')}</p>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">{t('skillDev.skill', 'Skill')}</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">{t('skillDev.demand', 'Demand')}</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">{t('skillDev.supply', 'Supply')}</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">{t('skillDev.gap', 'Gap')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {latest.slice(0, 30).map((s) => {
              const gap = s.demand_count - s.supply_count;
              return (
                <tr key={s.skill_name} className={gap > 0 ? 'bg-red-50' : ''}>
                  <td className="px-4 py-3 font-medium text-gray-900">{s.skill_name}</td>
                  <td className="px-4 py-3 text-right text-gray-700">{s.demand_count}</td>
                  <td className="px-4 py-3 text-right text-gray-700">{s.supply_count}</td>
                  <td className={['px-4 py-3 text-right font-semibold', gap > 0 ? 'text-red-600' : 'text-green-600'].join(' ')}>{gap > 0 ? `+${gap}` : gap}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// Shared components
// -------------------------------------------------------------------------

function EmployeeSelector({ employees, selectedId, onChange }: {
  employees: EmployeeListItem[];
  selectedId: string;
  onChange: (id: string) => void;
}) {
  const { t } = useTranslation();
  const options = useMemo(() =>
    employees.map((emp) => ({
      value: emp.id,
      label: `${emp.full_name} (${emp.department ?? '—'})`,
    })),
    [employees],
  );
  const selected = options.find((o) => o.value === selectedId) ?? null;

  return (
    <div className="max-w-md">
      <label id="sd-employee-label" className="block text-sm font-medium text-gray-700 mb-1">
        {t('skillDev.selectEmployee', 'Select an employee')}
      </label>
      <Select
        aria-labelledby="sd-employee-label"
        options={options}
        value={selected}
        onChange={(opt) => onChange(opt?.value ?? '')}
        isClearable
        placeholder={t('skillDev.chooseEmployee', '-- Choose an employee --')}
        classNamePrefix="react-select"
        styles={{
          control: (base) => ({ ...base, fontSize: '0.875rem', minHeight: '38px' }),
          option: (base) => ({ ...base, fontSize: '0.875rem' }),
        }}
      />
    </div>
  );
}

function LoadingSpinner() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center py-12" role="status">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-brand-500" />
      <span className="sr-only">{t('common.loading', 'Loading...')}</span>
    </div>
  );
}

function ErrorMessage({ message }: { message: string }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700" role="alert">
      {t('common.error', 'Error')}: {message}
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center text-sm text-gray-500">
      {message}
    </div>
  );
}
